import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { banksAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Landmark, Plus, Pencil, Trash2, Loader2, Save } from 'lucide-react';

const BankManager = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ bank_name: '', account_name: '', account_number: '', account_type: 'savings', ifsc_code: '', branch: '' });

  useEffect(() => { fetchBanks(); }, []);

  const fetchBanks = async () => {
    try { const r = await banksAPI.list(); setBanks(r.data || []); } catch {} finally { setLoading(false); }
  };

  const resetForm = () => { setForm({ bank_name: '', account_name: '', account_number: '', account_type: 'savings', ifsc_code: '', branch: '' }); setEditId(null); };

  const openEdit = (bank) => { setForm({ bank_name: bank.bank_name, account_name: bank.account_name, account_number: bank.account_number, account_type: bank.account_type, ifsc_code: bank.ifsc_code, branch: bank.branch || '' }); setEditId(bank.id); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.bank_name || !form.account_name || !form.account_number || !form.ifsc_code) { toast.error('Fill all required fields'); return; }
    setSaving(true);
    try {
      if (editId) { await banksAPI.update(editId, form); toast.success('Bank updated'); }
      else { await banksAPI.create(form); toast.success('Bank added'); }
      fetchBanks(); setDialogOpen(false); resetForm();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this bank account?')) return;
    try { await banksAPI.delete(id); toast.success('Bank deleted'); fetchBanks(); } catch { toast.error('Failed'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5 animate-fade-in" data-testid="bank-manager-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Bank Manager</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage bank accounts for payments</p>
          </div>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#4680ff] hover:bg-[#2563eb] text-white shadow-lg shadow-blue-500/20 rounded-lg h-10 px-5 text-sm font-semibold btn-press" data-testid="add-bank-btn">
            <Plus className="w-4 h-4 mr-2" />Add Bank
          </Button>
        </div>

        <Card className="border border-slate-100 shadow-sm rounded-xl overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-5 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}</div>
            ) : banks.length === 0 ? (
              <div className="text-center py-16"><Landmark className="w-12 h-12 mx-auto mb-3 text-slate-200" /><p className="text-sm text-slate-400 font-medium">No bank accounts added yet</p></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bank Name</TableHead>
                    <TableHead>Account Name</TableHead>
                    <TableHead>Account No.</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>IFSC</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {banks.map(b => (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">{b.bank_name}</TableCell>
                      <TableCell>{b.account_name}</TableCell>
                      <TableCell><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{b.account_number}</code></TableCell>
                      <TableCell className="capitalize">{b.account_type}</TableCell>
                      <TableCell className="font-mono text-sm">{b.ifsc_code}</TableCell>
                      <TableCell>{b.branch || '-'}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${b.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-red-50 text-red-700 border-red-100'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${b.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                          {b.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`edit-bank-${b.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(b.id)} data-testid={`delete-bank-${b.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent data-testid="bank-dialog">
            <DialogHeader><DialogTitle>{editId ? 'Edit Bank' : 'Add New Bank'}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Bank Name *</Label><Input placeholder="State Bank of India" value={form.bank_name} onChange={e => setForm({...form, bank_name: e.target.value})} data-testid="bank-name-input" /></div>
              <div className="space-y-2"><Label>Account Holder Name *</Label><Input placeholder="Account holder name" value={form.account_name} onChange={e => setForm({...form, account_name: e.target.value})} data-testid="account-name-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Account Number *</Label><Input placeholder="XXXXXXXXXXXX" value={form.account_number} onChange={e => setForm({...form, account_number: e.target.value})} data-testid="account-number-input" /></div>
                <div className="space-y-2"><Label>Account Type</Label>
                  <Select value={form.account_type} onValueChange={v => setForm({...form, account_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="savings">Savings</SelectItem><SelectItem value="current">Current</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>IFSC Code *</Label><Input placeholder="SBIN0001234" value={form.ifsc_code} onChange={e => setForm({...form, ifsc_code: e.target.value.toUpperCase()})} data-testid="ifsc-input" /></div>
                <div className="space-y-2"><Label>Branch</Label><Input placeholder="Branch name" value={form.branch} onChange={e => setForm({...form, branch: e.target.value})} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} style={{ background: '#1e1b4b' }} className="text-white" data-testid="save-bank-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{editId ? 'Update' : 'Add'} Bank
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default BankManager;
